import { useMemo, useState } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ClassSummary, type PeriodSummary } from '../lib/api';
import { Empty, PageHeading, Spinner } from '../components/ui';
import { Confirm } from '../components/Confirm';
import { AddForm, AddToggles, type Disclosure } from '../components/AddRecord';
import { CsvImport } from '../components/CsvImport';
import {
  Ledger,
  LedgerToolbar,
  onStage,
  statusFacet,
  searchRows,
  type LedgerColumn,
} from '../components/Ledger';
import { useListView } from '../lib/useListView';
import { useIsPlatformAdmin } from '../lib/useSession';

export function ManageClasses() {
  return (
    <div className="grid gap-10">
      <Periods />
      <Classes />
    </div>
  );
}

/*
 * A factory rather than a constant, because the actions cell needs the page's
 * mutation. No `search` accessors: this list has no search box, and declaring
 * searchable text nothing can search would be a claim with no control behind it.
 */
function periodColumns(actions: {
  /** Retire asks first and waits on this; Restore is one click. */
  onRetire: (period: PeriodSummary) => Promise<unknown>;
  busy: (period: PeriodSummary) => boolean;
}): LedgerColumn<PeriodSummary>[] {
  return [
    {
      id: 'name',
      header: 'Period',
      sort: (period) => period.name,
      className: 'font-medium',
      cell: (period) => (
        <Link
          to="/manage/periods/$periodId"
          params={{ periodId: period.id }}
          className={
            period.isActive ? 'hover:underline' : 'text-ink-faint line-through hover:text-ink'
          }
        >
          {period.name}
        </Link>
      ),
    },
    {
      id: 'classes',
      header: 'Classes',
      numeric: true,
      sort: (period) => period.classCount,
      className: 'font-mono tabular',
      /*
       * ink-soft rather than the ink-faint the class table uses for its own
       * zero. This cell is the only place the fact appears, so it is
       * information rather than decoration, and ink-faint measures 2.54:1
       * against the sheet in the light theme where AA asks 4.5:1.
       */
      /*
       * A link, because the number was the complaint: it said a period was used
       * by three classes and gave no way to find out which three. Zero is not a
       * link, since there is nothing on the other side of it to read.
       */
      cell: (period) =>
        period.classCount === 0 ? (
          <span className="text-ink-soft">none</span>
        ) : (
          <Link
            to="/manage/periods/$periodId"
            params={{ periodId: period.id }}
            className="hover:underline"
          >
            {period.classCount}
          </Link>
        ),
    },
    {
      id: 'actions',
      header: 'Actions',
      headerHidden: true,
      align: 'right',
      // No Manage: the row is the link to the period (see Ledger.tsx).
      cell: (period) => (
        <div className="flex flex-wrap justify-end gap-2">
          {period.isActive ? (
            <Confirm
              trigger="Retire"
              title={`Retire ${period.name}?`}
              description="It leaves every future register in this organization. Registers already marked keep it."
              confirmLabel="Retire"
              pendingLabel="Retiring…"
              disabled={actions.busy(period)}
              onConfirm={() => actions.onRetire(period)}
              errorFallback="Could not retire that period."
            />
          ) : (
            <button
              type="button"
              className="btn"
              title="Bring this period back onto new registers"
              onClick={() => void actions.onRetire(period).catch(() => {})}
              disabled={actions.busy(period)}
            >
              Restore
            </button>
          )}
        </div>
      ),
    },
  ];
}

/**
 * Organization-level periods: the vocabulary every class draws its register
 * columns from. Defined once here, attached per class on the class's own page.
 */
function Periods() {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [panel, setPanel] = useState<Disclosure>(null);

  const search = useSearch({ from: '/app/manage/classes' });
  const [shut, setShut] = useState<ReadonlySet<string>>(new Set());

  const periods = useQuery({ queryKey: ['periods'], queryFn: api.admin.periods });

  /*
   * A new period is attached to nothing, so it cannot be on any register and
   * only this list changes. Keeping it separate from the wider refresh below
   * means adding one does not refetch every cached register.
   */
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['periods'] });

  /*
   * Renaming or retiring one is different: the name is the heading of a
   * register column, and retiring drops the column from every future date. So
   * the register and the class detail page both have to be refetched, by
   * prefix, across every class and date they have cached.
   *
   * `['classes']` is deliberately not among them. Its periodCount counts
   * attachments, which neither renaming nor retiring changes. And `['class']`
   * does not match it: keys compare element by element.
   */
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['periods'] }),
      queryClient.invalidateQueries({ queryKey: ['class'] }),
      queryClient.invalidateQueries({ queryKey: ['grid'] }),
    ]);
  };

  const create = useMutation({
    mutationFn: () =>
      api.admin.createPeriod({ name, sortOrder: periods.data?.periods.length ?? 0 }),
    onSuccess: async () => {
      setName('');
      setPanel(null);
      await invalidate();
    },
  });

  /**
   * Retiring, not deleting. Deleting a period cascades through class_sessions
   * into attendance_records and would destroy every mark ever taken against it.
   */
  const retire = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.admin.updatePeriod(id, { isActive }),
    onSuccess: refresh,
  });

  const columns = useMemo(
    () =>
      periodColumns({
        onRetire: (period) => retire.mutateAsync({ id: period.id, isActive: !period.isActive }),
        // Per row, so one pending retire does not read as the whole list going dead.
        busy: (period) => retire.isPending && retire.variables?.id === period.id,
      }),
    [retire],
  );
  const view = useListView({ search, columns, prefix: 'period' });
  const show = view.get('period_show');
  const isPlatformAdmin = useIsPlatformAdmin();

  const all = useMemo(() => periods.data?.periods ?? [], [periods.data]);
  const rows = useMemo(() => onStage(all, show), [all, show]);

  return (
    <section>
      <PageHeading eyebrow="The columns of every register" title="Periods">
        <AddToggles panel={panel} onChange={setPanel} add="Add period" importable />
      </PageHeading>

      {panel === 'import' ? (
        <CsvImport entity="periods" onImported={invalidate} onClose={() => setPanel(null)} />
      ) : null}

      {panel === 'add' ? (
        <AddForm
          error={create.error}
          fallback="Could not add period."
          pending={create.isPending}
          submit="Add period"
          onSubmit={() => create.mutate()}
        >
          <label className="grid gap-1.5">
            <span className="eyebrow">Name</span>
            <input
              className="field"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Lesson"
            />
          </label>
        </AddForm>
      ) : null}

      <div>
        <LedgerToolbar
          facets={[
            statusFacet({
              label: 'Filter periods by status',
              liveLabel: 'Active',
              inactiveLabel: 'Retired',
              value: show,
              onChange: (next) => view.set({ period_show: next || undefined }),
              live: onStage(all, '').length,
              inactive: onStage(all, 'inactive').length,
              deleted: isPlatformAdmin ? onStage(all, 'deleted').length : undefined,
            }),
          ]}
          shown={rows.length}
          total={all.length}
        />
        <Ledger
          caption="Periods"
          rows={rows}
          total={all.length}
          columns={columns}
          rowId={(period) => period.id}
          sort={view.sort}
          onSort={view.onSort}
          collapsed={shut}
          onCollapse={(id, closed) =>
            setShut((prev) => {
              const next = new Set(prev);
              if (closed) next.add(id);
              else next.delete(id);
              return next;
            })
          }
          /*
           * The only place the app says what a period is, shown exactly when
           * somebody needs to know: they have none, and the button above is
           * how to add one. The word is overloaded, meaning a timetable slot
           * nearly everywhere else, so "add the first one" on its own would
           * not answer what to type.
           */
          empty={
            <p className="mb-4 max-w-2xl text-sm text-ink-soft">
              None yet. A period is a part of a meeting you mark separately: "Worship, Lesson,
              Activity" for a Sunday school, or "Period 1" through "Period 8" for a school day. Add
              one from the button above, then attach it to a class from that class's page.
            </p>
          }
          noMatch={
            <p className="mb-4 text-sm text-ink-soft">
              {show === 'deleted'
                ? 'No period here has been deleted.'
                : show === 'inactive'
                  ? 'No period here is retired.'
                  : 'Every period here is retired. Switch to Retired to see them.'}
            </p>
          }
        />
      </div>
    </section>
  );
}

/*
 * A retired class is struck through rather than labelled, matching the periods
 * list above and the roll on a class's own page. The group heading already says
 * which half of the table you are reading, so the word would be said twice.
 *
 * No actions column. The name is a link, which makes the whole row one (see
 * Ledger.tsx), and a Manage button beside it went to the same place.
 */
const COLUMNS: LedgerColumn<ClassSummary>[] = [
  {
    id: 'name',
    header: 'Class',
    sort: (klass) => klass.name,
    search: (klass) => klass.name,
    className: 'font-medium',
    cell: (klass) => (
      <Link
        to="/manage/classes/$classId"
        params={{ classId: klass.id }}
        className={
          klass.isActive ? 'hover:underline' : 'text-ink-faint line-through hover:text-ink'
        }
      >
        {klass.name}
      </Link>
    ),
  },
  {
    id: 'students',
    header: 'Students',
    numeric: true,
    sort: (klass) => klass.studentCount,
    className: 'font-mono tabular',
    cell: (klass) => klass.studentCount,
  },
  {
    id: 'periods',
    header: 'Periods',
    numeric: true,
    sort: (klass) => klass.periodCount,
    className: 'font-mono tabular',
    cell: (klass) =>
      klass.periodCount === 0 ? (
        <span className="text-ink-faint" title="Nothing to mark until a period is attached">
          none yet
        </span>
      ) : (
        klass.periodCount
      ),
  },
];

function Classes() {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const search = useSearch({ from: '/app/manage/classes' });
  const view = useListView({ search, columns: COLUMNS, prefix: 'class' });
  const show = view.get('class_show');
  const isPlatformAdmin = useIsPlatformAdmin();
  /*
   * Collapse is deliberately not in the address. It is chrome rather than a
   * filter, and a group left collapsed would print as a heading with nothing
   * under it, which is a silently incomplete page. Rows expand in place, so
   * nothing navigates away and open groups survive the whole visit regardless.
   */
  const [shut, setShut] = useState<ReadonlySet<string>>(new Set());

  const classes = useQuery({ queryKey: ['classes'], queryFn: api.classes.list });
  const [panel, setPanel] = useState<Disclosure>(null);

  /*
   * A classes file attaches periods and assigns staff as well, so the pages
   * that count either (a period's classes, a person's classes) and every
   * register are refetched along with the list.
   */
  const imported = () =>
    Promise.all(
      [['classes'], ['class'], ['periods'], ['period'], ['users'], ['user'], ['grid']].map(
        (queryKey) => queryClient.invalidateQueries({ queryKey }),
      ),
    );

  const create = useMutation({
    mutationFn: () => api.admin.createClass({ name, periodIds: [], teacherIds: [] }),
    onSuccess: async () => {
      setName('');
      setPanel(null);
      await queryClient.invalidateQueries({ queryKey: ['classes'] });
    },
  });

  const all = useMemo(() => classes.data?.classes ?? [], [classes.data]);
  const rows = useMemo(
    () => searchRows(onStage(all, show), COLUMNS, view.query),
    [all, show, view.query],
  );

  return (
    <section>
      <PageHeading eyebrow="Rosters and schedules" title="Classes">
        <AddToggles panel={panel} onChange={setPanel} add="Add class" importable />
      </PageHeading>

      {panel === 'import' ? (
        <CsvImport entity="classes" onImported={imported} onClose={() => setPanel(null)} />
      ) : null}

      {panel === 'add' ? (
        <AddForm
          error={create.error}
          fallback="Could not add class."
          pending={create.isPending}
          submit="Add class"
          onSubmit={() => create.mutate()}
        >
          <label className="grid gap-1.5">
            <span className="eyebrow">Name</span>
            <input
              className="field"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Grade 3"
            />
          </label>
        </AddForm>
      ) : null}

      {classes.isLoading ? (
        <Spinner />
      ) : (
        <>
          <LedgerToolbar
            facets={[
              statusFacet({
                label: 'Filter classes by status',
                liveLabel: 'Active',
                inactiveLabel: 'Retired',
                value: show,
                onChange: (next) => view.set({ class_show: next || undefined }),
                live: onStage(all, '').length,
                inactive: onStage(all, 'inactive').length,
                deleted: isPlatformAdmin ? onStage(all, 'deleted').length : undefined,
              }),
            ]}
            query={view.draft}
            onQuery={view.onQuery}
            searchPlaceholder="class name"
            shown={rows.length}
            total={all.length}
          />
          <Ledger
            caption="Classes"
            rows={rows}
            total={all.length}
            columns={COLUMNS}
            rowId={(klass) => klass.id}
            sort={view.sort}
            onSort={view.onSort}
            collapsed={shut}
            onCollapse={(id, closed) =>
              setShut((prev) => {
                const next = new Set(prev);
                if (closed) next.add(id);
                else next.delete(id);
                return next;
              })
            }
            empty={
              <Empty
                title="No classes yet"
                hint="Add a class, then attach periods and enrol students."
              />
            }
            noMatch={
              <Empty
                title="Nothing to show"
                hint={
                  view.query
                    ? 'No class here matches that name.'
                    : show === 'deleted'
                      ? 'No class here has been deleted.'
                      : show === 'inactive'
                        ? 'No class here is retired.'
                        : 'They are all retired. Switch to Retired to see them.'
                }
              />
            }
          />
        </>
      )}
    </section>
  );
}
